import React, { useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  Tabs,
  Table,
  Collapse,
  Alert,
  Tag,
  Space,
  Button,
  Typography,
  Row,
  Col,
  Statistic
} from 'antd';
import { useBackdropClose } from '../hooks/useBackdropClose';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';

const { Panel } = Collapse;
const { Text, Title } = Typography;

/**
 * Компонент для отображения детальной статистики синхронизации
 * Использует современные практики React (Context7):
 * - Функциональный компонент с React Hooks
 * - useMemo для мемоизации вычисляемых данных
 * - useCallback для обработчиков
 * - PropTypes для типизации
 */
const SyncStatsModal = ({
  visible,
  onClose,
  stats,
  title = 'Статистика синхронизации',
  zIndex
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [copied, setCopied] = useState(false);

  // Мемоизация вычисляемых данных
  const summaryData = useMemo(() => {
    if (!stats) return null;
    
    // Определяем тип статистики (прокси, модемы, полная синхронизация)
    if (stats.summary) {
      const summary = stats.summary;
      
      // Полная синхронизация
      if (summary.modems && summary.proxies && summary.clients) {
        return {
          type: 'full',
          totalConnections: summary.connections_total || 0,
          successfulConnections: summary.connections_successful || 0,
          failedConnections: summary.connections_failed || 0,
          modems: summary.modems,
          proxies: summary.proxies,
          clients: summary.clients
        };
      }
      
      // Синхронизация прокси
      if (summary.no_update_needed !== undefined) {
        return {
          type: 'proxies',
          totalProcessed: summary.total_processed || 0,
          added: summary.added || 0,
          updated: summary.updated || 0,
          noUpdateNeeded: summary.no_update_needed || 0,
          deleted: summary.deleted || 0,
          errors: summary.errors || 0,
          connectionsProcessed: summary.connections_processed || 0,
          connectionsFailed: summary.connections_failed || 0
        };
      }
      
      // Синхронизация модемов
      if (summary.total_connections !== undefined) {
        return {
          type: 'modems',
          totalConnections: summary.total_connections || 0,
          successfulConnections: summary.successful_connections || 0,
          failedConnections: summary.failed_connections || 0,
          totalModemsAdded: summary.total_modems_added || 0,
          totalModemsUpdated: summary.total_modems_updated || 0,
          totalModemsRemoved: summary.total_modems_removed || 0,
          errors: summary.errors || 0
        };
      }
      // Массовое обновление модемов
      if (summary.total_updated !== undefined || summary.total_errors !== undefined) {
        return {
          type: 'bulk_update_modems',
          totalUpdated: summary.total_updated || 0,
          totalErrors: summary.total_errors || 0,
          totalConnections: (stats.details && stats.details.length) || 0
        };
      }
    }
    
    return null;
  }, [stats]);

  // Мемоизация данных для таблицы подключений
  const connectionTableData = useMemo(() => {
    if (!stats || !stats.details) return [];
    
    return stats.details.map((detail, index) => ({
      key: detail.connection_id || index,
      connectionId: detail.connection_id,
      connectionName: detail.connection_name || `Подключение ${detail.connection_id}`,
      ...detail
    }));
  }, [stats]);

  // Мемоизация колонок для таблицы подключений (прокси)
  const proxyColumns = useMemo(() => [
    {
      title: 'Подключение',
      dataIndex: 'connectionName',
      key: 'connectionName',
      fixed: 'left',
      width: 200
    },
    {
      title: 'Добавлено',
      dataIndex: 'added',
      key: 'added',
      width: 100,
      render: (value) => (
        <Tag color="green">{value || 0}</Tag>
      )
    },
    {
      title: 'Обновлено',
      dataIndex: 'updated',
      key: 'updated',
      width: 100,
      render: (value) => (
        <Tag color="blue">{value || 0}</Tag>
      )
    },
    {
      title: 'Не требовало обновления',
      dataIndex: 'no_update_needed',
      key: 'no_update_needed',
      width: 180,
      render: (value) => (
        <Tag color="default">{value || 0}</Tag>
      )
    },
    {
      title: 'Удалено',
      dataIndex: 'deleted',
      key: 'deleted',
      width: 100,
      render: (value) => (
        <Tag color="orange">{value || 0}</Tag>
      )
    },
    {
      title: 'Ошибок',
      dataIndex: 'errors',
      key: 'errors',
      width: 100,
      render: (value) => (
        <Tag color={value > 0 ? 'red' : 'green'}>
          {value || 0}
        </Tag>
      )
    }
  ], []);

  // Мемоизация колонок для таблицы подключений (модемы)
  const modemColumns = useMemo(() => [
    {
      title: 'Подключение',
      dataIndex: 'connectionName',
      key: 'connectionName',
      fixed: 'left',
      width: 200
    },
    {
      title: 'Добавлено',
      dataIndex: 'added',
      key: 'added',
      width: 100,
      render: (value) => (
        <Tag color="green">{value || 0}</Tag>
      )
    },
    {
      title: 'Обновлено',
      dataIndex: 'updated',
      key: 'updated',
      width: 100,
      render: (value) => (
        <Tag color="blue">{value || 0}</Tag>
      )
    },
    {
      title: 'Удалено',
      dataIndex: 'removed',
      key: 'removed',
      width: 100,
      render: (value) => (
        <Tag color="orange">{value || 0}</Tag>
      )
    },
    {
      title: 'Ошибок',
      dataIndex: 'errors',
      key: 'errors',
      width: 150,
      render: (errors) => {
        const errorCount = Array.isArray(errors) ? errors.length : (errors || 0);
        return (
          <Tag color={errorCount > 0 ? 'red' : 'green'}>
            {errorCount}
          </Tag>
        );
      }
    }
  ], []);

  // Колонки для массового обновления модемов (по подключениям)
  const bulkUpdateModemColumns = useMemo(() => [
    {
      title: 'Подключение',
      dataIndex: 'connectionName',
      key: 'connectionName',
      fixed: 'left',
      width: 200
    },
    {
      title: 'Обновлено',
      dataIndex: 'updated',
      key: 'updated',
      width: 120,
      render: (value) => (
        <Tag color="blue">{value || 0}</Tag>
      )
    },
    {
      title: 'Ошибок',
      dataIndex: 'errors',
      key: 'errors',
      width: 100,
      render: (value) => (
        <Tag color={value > 0 ? 'red' : 'green'}>
          {value || 0}
        </Tag>
      )
    }
  ], []);

  // Мемоизация колонок для таблицы подключений (полная синхронизация)
  const fullSyncColumns = useMemo(() => [
    {
      title: 'Подключение',
      dataIndex: 'connectionName',
      key: 'connectionName',
      fixed: 'left',
      width: 200
    },
    {
      title: 'Модемы',
      key: 'modems',
      children: [
        {
          title: 'Добавлено',
          dataIndex: ['modems', 'added'],
          key: 'modems_added',
          width: 80,
          render: (value) => <Tag color="green">{value || 0}</Tag>
        },
        {
          title: 'Обновлено',
          dataIndex: ['modems', 'updated'],
          key: 'modems_updated',
          width: 80,
          render: (value) => <Tag color="blue">{value || 0}</Tag>
        },
        {
          title: 'Удалено',
          dataIndex: ['modems', 'removed'],
          key: 'modems_removed',
          width: 80,
          render: (value) => <Tag color="orange">{value || 0}</Tag>
        }
      ]
    },
    {
      title: 'Прокси',
      key: 'proxies',
      children: [
        {
          title: 'Добавлено',
          dataIndex: ['proxies', 'added'],
          key: 'proxies_added',
          width: 80,
          render: (value) => <Tag color="green">{value || 0}</Tag>
        },
        {
          title: 'Обновлено',
          dataIndex: ['proxies', 'updated'],
          key: 'proxies_updated',
          width: 80,
          render: (value) => <Tag color="blue">{value || 0}</Tag>
        },
        {
          title: 'Не требовало обновления',
          dataIndex: ['proxies', 'no_update_needed'],
          key: 'proxies_no_update',
          width: 150,
          render: (value) => <Tag color="default">{value || 0}</Tag>
        },
        {
          title: 'Удалено',
          dataIndex: ['proxies', 'deleted'],
          key: 'proxies_deleted',
          width: 80,
          render: (value) => <Tag color="orange">{value || 0}</Tag>
        }
      ]
    },
    {
      title: 'Клиенты',
      key: 'clients',
      children: [
        {
          title: 'Добавлено',
          dataIndex: ['clients', 'added'],
          key: 'clients_added',
          width: 80,
          render: (value) => <Tag color="green">{value || 0}</Tag>
        },
        {
          title: 'Обновлено',
          dataIndex: ['clients', 'updated'],
          key: 'clients_updated',
          width: 80,
          render: (value) => <Tag color="blue">{value || 0}</Tag>
        }
      ]
    },
    {
      title: 'Ошибок',
      dataIndex: 'errors',
      key: 'errors',
      width: 100,
      render: (errors) => {
        const errorCount = Array.isArray(errors) ? errors.length : (errors || 0);
        return (
          <Tag color={errorCount > 0 ? 'red' : 'green'}>
            {errorCount}
          </Tag>
        );
      }
    }
  ], []);

  // Мемоизация списка всех ошибок
  const allErrors = useMemo(() => {
    const errors = [];
    if (!stats) return errors;
    if (stats.details) {
      stats.details.forEach((detail) => {
        const errorDetails = detail.error_details || [];
        const connectionErrors = detail.errors || [];
        if (Array.isArray(connectionErrors)) {
          connectionErrors.forEach((error) => {
            errors.push({
              connection: detail.connection_name || `Подключение ${detail.connection_id}`,
              error: typeof error === 'string' ? error : JSON.stringify(error)
            });
          });
        }
        errorDetails.forEach((error) => {
          errors.push({
            connection: detail.connection_name || `Подключение ${detail.connection_id}`,
            error: typeof error === 'string' ? error : JSON.stringify(error)
          });
        });
      });
    }
    if (stats.error_details && Array.isArray(stats.error_details)) {
      stats.error_details.forEach((item) => {
        errors.push({
          connection: item.connection_name || `Подключение ${item.connection_id || item.port_id}`,
          error: typeof item.error === 'string' ? item.error : JSON.stringify(item.error)
        });
      });
    }
    return errors;
  }, [stats]);

  // Обработчик копирования статистики
  const handleCopyStats = useCallback(() => {
    if (!stats) return;
    
    const statsText = JSON.stringify(stats, null, 2);
    navigator.clipboard.writeText(statsText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch((err) => {
      console.error('Ошибка копирования:', err);
    });
  }, [stats]);

  // Обработчик закрытия модального окна
  const handleClose = useCallback(() => {
    setActiveTab('summary');
    setCopied(false);
    onClose();
  }, [onClose]);

  // Затемнение закрывает окно только настоящим щелчком — нажатие и отпускание на нём (владелец 29.10, DF85)
  const modalBackdrop = useBackdropClose(handleClose);

  // Обработчик переключения табов
  const handleTabChange = useCallback((key) => {
    setActiveTab(key);
  }, []);

  if (!visible || !stats) return null;

  const tabItems = [];

  // Таб "Общая статистика"
  if (summaryData) {
    tabItems.push({
      key: 'summary',
      label: 'Общая статистика',
      children: (
        <div>
          {summaryData.type === 'full' && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic
                  title="Всего подключений"
                  value={summaryData.totalConnections}
                  valueStyle={{ color: '#ffffff' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="Успешно"
                  value={summaryData.successfulConnections}
                  valueStyle={{ color: '#4cd9a5' }}
                  prefix={<CheckCircleOutlined />}
                />
              </Col>
              <Col span={8}>
                <Statistic
                  title="С ошибками"
                  value={summaryData.failedConnections}
                  valueStyle={{ color: '#f77' }}
                  prefix={<CloseCircleOutlined />}
                />
              </Col>
            </Row>
          )}
          
          {summaryData.type === 'proxies' && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={6}>
                <Statistic title="Добавлено" value={summaryData.added} valueStyle={{ color: '#4cd9a5' }} />
              </Col>
              <Col span={6}>
                <Statistic title="Обновлено" value={summaryData.updated} valueStyle={{ color: '#7aa3f7' }} />
              </Col>
              <Col span={6}>
                <Statistic title="Не требовало обновления" value={summaryData.noUpdateNeeded} valueStyle={{ color: '#ffffff' }} />
              </Col>
              <Col span={6}>
                <Statistic title="Удалено" value={summaryData.deleted} valueStyle={{ color: '#faad14' }} />
              </Col>
            </Row>
          )}
          
          {summaryData.type === 'modems' && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic title="Добавлено" value={summaryData.totalModemsAdded} valueStyle={{ color: '#4cd9a5' }} />
              </Col>
              <Col span={8}>
                <Statistic title="Обновлено" value={summaryData.totalModemsUpdated} valueStyle={{ color: '#7aa3f7' }} />
              </Col>
              <Col span={8}>
                <Statistic title="Удалено" value={summaryData.totalModemsRemoved} valueStyle={{ color: '#faad14' }} />
              </Col>
            </Row>
          )}
          {summaryData.type === 'bulk_update_modems' && (
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Statistic title="Обновлено" value={summaryData.totalUpdated} valueStyle={{ color: '#7aa3f7' }} />
              </Col>
              <Col span={8}>
                <Statistic title="Ошибок" value={summaryData.totalErrors} valueStyle={{ color: summaryData.totalErrors > 0 ? '#f77' : '#4cd9a5' }} />
              </Col>
              <Col span={8}>
                <Statistic title="Подключений" value={summaryData.totalConnections} valueStyle={{ color: '#ffffff' }} />
              </Col>
            </Row>
          )}
          {stats.message && (
            <Alert
              message={stats.message}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
        </div>
      )
    });
  }

  // Таб "По подключениям"
  if (connectionTableData.length > 0) {
    let columns = [];
    if (summaryData?.type === 'full') {
      columns = fullSyncColumns;
    } else if (summaryData?.type === 'modems') {
      columns = modemColumns;
    } else if (summaryData?.type === 'bulk_update_modems') {
      columns = bulkUpdateModemColumns;
    } else {
      columns = proxyColumns;
    }
    
    tabItems.push({
      key: 'connections',
      label: 'По подключениям',
      children: (
        <Table
          dataSource={connectionTableData}
          columns={columns}
          pagination={false}
          scroll={{ x: 'max-content' }}
          size="small"
        />
      )
    });
  }

  // Таб "Ошибки"
  if (allErrors.length > 0) {
    tabItems.push({
      key: 'errors',
      label: `Ошибки (${allErrors.length})`,
      children: (
        <Collapse>
          {allErrors.map((errorItem, index) => (
            <Panel
              header={
                <Space>
                  <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                  <Text strong>{errorItem.connection}</Text>
                </Space>
              }
              key={index}
            >
              <Text code style={{ whiteSpace: 'pre-wrap' }}>
                {errorItem.error}
              </Text>
            </Panel>
          ))}
        </Collapse>
      )
    });
  }

  return (
    <Modal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>{title}</Title>
          <Button
            icon={<CopyOutlined />}
            size="small"
            onClick={handleCopyStats}
          >
            {copied ? 'Скопировано!' : 'Копировать'}
          </Button>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      maskClosable={false}
      wrapProps={modalBackdrop}
      footer={[
        <Button key="close" onClick={handleClose}>
          Закрыть
        </Button>
      ]}
      width={1000}
      style={{ top: 20 }}
      zIndex={zIndex}
      className="sync-stats-modal--light"
    >
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
      />
    </Modal>
  );
};

SyncStatsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stats: PropTypes.object,
  title: PropTypes.string,
  zIndex: PropTypes.number
};

export default SyncStatsModal;
